// Universal DeskeyHUB document viewer modal — online viewable, not just
// downloadable. Shared across student (StudentHubPage), teacher
// (TeacherHubBrowser), and institution-admin (DeskeyHubSection) portals —
// pass `accent` to match the caller's brand color.
//
// Detects the file extension from doc.file_ext OR the file_name and embeds:
//   • PDF (.pdf)                       → native <iframe> (browser renderer)
//   • Office (.ppt/.pptx/.doc/.docx/   → Google Docs Viewer
//             .xls/.xlsx)                (`docs.google.com/gview?url=…&embedded=true`)
//                                        with a "Try Microsoft Office Online"
//                                        secondary action for stubborn files.
//   • Images                           → native <img>
//   • Plain text (.txt/.md/.csv)       → fetched + rendered as <pre>
//   • Other                            → friendly "Download to open" CTA.
//
// PDF / image / new-tab views fetch with the Authorization header and render
// a blob: URL (lib/authedFile.js). Only the Office viewers still get a
// `?token=` URL: Google/Microsoft fetch the file from their own servers, so
// a blob: URL can't work there. That token is a 5-minute one that opens only
// this file (POST …/file-token), never the login token.
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { X, Download, ExternalLink, FileText, Image as ImgIcon, Loader2, Eye } from 'lucide-react';
import { useAuthedObjectUrl, openFileInNewTab, fetchFileBlob } from '../lib/authedFile';
import Loader from './Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFICE_EXTS = new Set(['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx', 'odt', 'ods', 'odp']);
const TEXT_EXTS   = new Set(['txt', 'md', 'csv', 'json', 'log']);
const IMG_EXTS    = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']);

function detectExt(doc) {
  if (doc.file_ext) return doc.file_ext.toLowerCase().replace(/^\./, '');
  const fn = doc.file_name || doc.title || '';
  const m = fn.match(/\.([a-z0-9]{2,5})$/i);
  return m ? m[1].toLowerCase() : '';
}

export default function DocViewer({ doc, departmentId, onClose, accent = '#0d9488' }) {
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(false);
  // Toggle between Google Docs Viewer (default) and Microsoft Office Online
  // for Office files — if one provider can't render the file, the user can
  // flip to the other with a single click.
  const [officeProvider, setOfficeProvider] = useState('google');

  const ext = useMemo(() => detectExt(doc), [doc]);
  const token = localStorage.getItem('desky_token') || '';
  const baseUrl = `${API}/hub/repo/${departmentId || doc.department_id}/documents/${doc.id}/download`;
  // Third-party Office viewers fetch the file themselves — see header note.
  // null = loading, '' = failed.
  const [officeUrl, setOfficeUrl] = useState(null);
  const downloadUrl = `${baseUrl}?dl=1`;
  const [blobUrl, blobFailed] = useAuthedObjectUrl(ext === 'pdf' || IMG_EXTS.has(ext) ? baseUrl : null);
  const blobPending = !blobUrl && (blobFailed
    ? <p className="text-sm p-8 text-center opacity-70">Could not load file.</p>
    : <Loader />);
  const openNewTab = () => openFileInNewTab(baseUrl).catch(() => toast.error('Could not open file'));

  // For text-type, fetch directly with a Bearer header
  useEffect(() => {
    let cancelled = false;
    if (TEXT_EXTS.has(ext)) {
      setLoading(true);
      fetchFileBlob(baseUrl)
        .then(b => b.text())
        .then(t => { if (!cancelled) setTextContent(t); })
        .catch(() => { if (!cancelled) setTextContent('Could not load file.'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => { cancelled = true; };
  }, [ext, baseUrl, token]);

  // Re-mint on provider switch: the token lives 5 minutes.
  useEffect(() => {
    if (!OFFICE_EXTS.has(ext)) return undefined;
    let cancelled = false;
    setOfficeUrl(null);
    fetch(baseUrl.replace(/\/download$/, '/file-token'), { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('token'))))
      .then(d => { if (!cancelled) setOfficeUrl(`${baseUrl}?token=${encodeURIComponent(d.token)}`); })
      .catch(() => { if (!cancelled) setOfficeUrl(''); });
    return () => { cancelled = true; };
  }, [ext, baseUrl, token, officeProvider]);

  const triggerDownload = async () => {
    try {
      const res = await fetch(downloadUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || doc.title || `document.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  let body;
  if (ext === 'pdf') {
    body = (
      blobPending || <iframe src={blobUrl} title={doc.title}
        className="w-full rounded-xl border bg-white"
        style={{ height: '74vh', borderColor: '#e5e7eb' }}
        data-testid="docviewer-pdf-iframe" />
    );
  } else if (OFFICE_EXTS.has(ext)) {
    const gview = `https://docs.google.com/gview?url=${encodeURIComponent(officeUrl)}&embedded=true`;
    const office = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(officeUrl)}`;
    body = (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 text-[11px]">
          <div className="opacity-70">
            Rendered via <b>{officeProvider === 'google' ? 'Google Docs Viewer' : 'Microsoft Office Online'}</b>.
            Preview not working? <button onClick={() => setOfficeProvider(officeProvider === 'google' ? 'office' : 'google')}
              data-testid="docviewer-office-toggle"
              className="font-semibold underline" style={{ color: accent }}>
              Try {officeProvider === 'google' ? 'Microsoft Office Online' : 'Google Docs Viewer'}
            </button>
          </div>
        </div>
        {officeUrl ? <iframe key={officeProvider}
          src={officeProvider === 'google' ? gview : office}
          title={doc.title}
          className="w-full rounded-xl border bg-white"
          style={{ height: '70vh', borderColor: '#e5e7eb' }}
          data-testid={`docviewer-office-${officeProvider}-iframe`} />
          : officeUrl === '' ? <p className="text-sm p-8 text-center opacity-70">Could not load file.</p>
          : <Loader />}
      </div>
    );
  } else if (IMG_EXTS.has(ext)) {
    body = (
      <div className="flex items-center justify-center bg-white rounded-xl border p-3" style={{ borderColor: '#e5e7eb' }}>
        {blobPending || <img src={blobUrl} alt={doc.title}
          className="max-h-[74vh] max-w-full rounded-lg"
          data-testid="docviewer-image" />}
      </div>
    );
  } else if (TEXT_EXTS.has(ext)) {
    body = (
      <div className="rounded-xl border bg-white p-4 text-[13px] font-mono whitespace-pre-wrap overflow-auto"
        style={{ borderColor: '#e5e7eb', maxHeight: '74vh' }}
        data-testid="docviewer-text">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (textContent || 'Empty.')}
      </div>
    );
  } else {
    body = (
      <div className="rounded-2xl border-2 p-8 text-center"
        style={{ borderColor: '#e5e7eb', background: '#f9fafb' }}>
        <FileText className="w-12 h-12 mx-auto opacity-30" />
        <p className="text-sm font-bold mt-3">Inline preview not supported for <span className="font-mono">.{ext || 'file'}</span></p>
        <p className="text-xs opacity-60 mt-1">Download it to open with the right app on your device.</p>
        <Button onClick={triggerDownload} className="rounded-full text-white mt-4" style={{ background: accent }} data-testid="docviewer-download-fallback">
          <Download className="w-4 h-4 mr-1.5" /> Download file
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="doc-viewer-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[94vh] overflow-auto"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center gap-3 sticky top-0 z-10 bg-white rounded-t-3xl"
          style={{ borderColor: '#e5e7eb' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0"
            style={{ background: accent }}>
            {IMG_EXTS.has(ext) ? <ImgIcon className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate text-slate-900">{doc.title || doc.file_name}</div>
            <div className="text-[11px] opacity-60 truncate">
              <span className="font-mono uppercase">.{ext || 'file'}</span>
              {doc.subject_code && <> · <span className="font-mono">{doc.subject_code}</span></>}
              {doc.subject && <> · {doc.subject}</>}
              {doc.department_name && <> · {doc.department_name}</>}
              {doc.file_size && <> · {formatBytes(doc.file_size)}</>}
            </div>
          </div>
          <Button onClick={triggerDownload} variant="ghost" className="rounded-full text-xs" data-testid="docviewer-download">
            <Download className="w-3.5 h-3.5 mr-1" /> Download
          </Button>
          <button type="button" onClick={openNewTab} aria-label="Open in new tab"
            className="rounded-full px-3 py-2 hover:bg-black/5 inline-flex items-center text-xs"
            title="Open in new tab" data-testid="docviewer-newtab">
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
          <button onClick={onClose} aria-label="Close" data-testid="docviewer-close"
            className="p-2 rounded-full hover:bg-black/5">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        {/* Body */}
        <div className="p-5">{body}</div>
      </div>
    </div>
  );
}

function formatBytes(b) {
  if (!b) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${units[i]}`;
}
